import React, { useEffect, useMemo, useState } from "react";
import axiosBackend from "../api/axiosBackend";

// shadcn
import { Popover, PopoverTrigger, PopoverContent } from "components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "components/ui/command";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Plus, X } from "lucide-react";

// Debounce small helper
function useDebounced(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

export default function AutocompleteTagInput({ selectedTags, setSelectedTags }) {
  const [allTags, setAllTags] = useState([]);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // initial fetch
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await axiosBackend.get("/tags/get");
        setAllTags(res.data);
      } catch (e) {
        console.error("Error fetching tags:", e);
      }
    };
    fetchTags();
  }, []);

  // filtered list (client + server for accuracy)
  useEffect(() => {
    const fetchFiltered = async () => {
      try {
        if (!debouncedQuery) return; // no need to fetch when empty
        const res = await axiosBackend.get(`/tags/get?name=${encodeURIComponent(debouncedQuery)}`);
        // merge unique by _id (server may return subset)
        const existing = new Map(allTags.map((t) => [t._id, t]));
        res.data.forEach((t) => existing.set(t._id, t));
        setAllTags(Array.from(existing.values()));
      } catch (e) {
        console.error("Error fetching filtered tags:", e);
      }
    };
    fetchFiltered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allTags;
    return allTags.filter((t) => t.name.toLowerCase().includes(q));
  }, [allTags, query]);

  const handleSelect = (tag) => {
    if (!selectedTags.some((t) => t._id === tag._id)) {
      setSelectedTags([...selectedTags, tag]);
    }
    setQuery("");
    setOpen(false);
  };

  const handleRemove = (id) => {
    setSelectedTags(selectedTags.filter((t) => t._id !== id));
  };

  const handleCreate = async () => {
    if (!query.trim()) return;
    setCreating(true);
    try {
      const res = await axiosBackend.post("/tags/add", { name: query.trim() });
      const newTag = res.data;
      setAllTags((prev) => [...prev, newTag]);
      handleSelect(newTag);
    } catch (e) {
      console.error("Error adding new tag:", e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="w-full" dir="rtl">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setOpen(true)}
              placeholder="התחל להקליד כדי לחפש תג"
              aria-label="חיפוש תגיות"
              className="pr-8"
            />
            {/* optional icon space on the right */}
          </div>
        </PopoverTrigger>

        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="end">
          <Command shouldFilter={false}>
            <CommandInput placeholder="חפש תג…" value={query} onValueChange={setQuery} />
            <CommandList className="max-h-64">
              {filtered.length === 0 ? (
                <CommandEmpty className="flex items-center justify-between px-3 py-2">
                  <span>לא נמצאו תגיות</span>
                  <Button size="sm" onClick={handleCreate} disabled={creating}>
                    <Plus className="mr-1 h-4 w-4" />
                    {creating ? "יוצר…" : `הוסף תג: "${query}"`}
                  </Button>
                </CommandEmpty>
              ) : (
                <CommandGroup>
                  {filtered.map((tag) => (
                    <CommandItem
                      key={tag._id}
                      value={tag.name}
                      onSelect={() => handleSelect(tag)}
                      className="cursor-pointer"
                    >
                      {tag.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected tags */}
      <div className="mt-2 flex flex-wrap gap-2">
        {selectedTags.map((tag) => (
          <Badge
            key={tag._id}
            variant="secondary"
            className="rounded-full px-3 py-1 text-sm bg-primary/10 text-primary border-transparent"
          >
            <span>{tag.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="ml-1 h-5 w-5 p-0"
              onClick={() => handleRemove(tag._id)}
              aria-label={`הסר תג ${tag.name}`}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </Badge>
        ))}
      </div>
    </div>
  );
}
